import { ComponentProps } from 'react';
import styled, { css } from 'styled-components';

import SC from './SC';

interface ToggleProps extends ComponentProps<typeof ToggleInput> {
  sc?: ToggleContainerProps;
}

export const Toggle = ({ id, sc, ...props }: ToggleProps) => {
  const identifier = id || `checkbox-${Math.round(Math.random() * 1000000)}`;

  return (
    <ToggleContainer htmlFor={identifier} sc={sc}>
      <ToggleInput type="checkbox" {...props} id={identifier} />
      <ToggleBackground>
        <ToggleSwitch />
      </ToggleBackground>
    </ToggleContainer>
  );
};

export const ToggleInput = styled.input`
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px 1px 1px 1px);
  clip: rect(1px, 1px, 1px, 1px);
`;

const ToggleBackground = styled.span`
  ${({ theme }) => css`
    position: relative;
    display: inline-block;
    border-radius: 20px;
    background: ${theme.transparentize(theme.saturate(theme.colors.gray[900], 100), 0.8)};
  `}
`;

const ToggleSwitch = styled.span`
  ${({ theme }) => css`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    display: inline-block;
    border-radius: 100%;
    top: 50%;
    transform: translateY(-50%) translateX(25%);
    background: ${theme.colors.white};
    transition: all 0.15s ease-in-out;
  `}
`;

interface ToggleContainerProps {
  size?: 'sm' | 'md';
}

export const ToggleContainer = styled.label<SC<ToggleContainerProps>>`
  ${({ theme, sc: { size = 'md' } = {} }) => css`
    display: inline-flex;
    vertical-align: middle;
    align-items: center;
    height: 24px;
    cursor: pointer;

    ${ToggleInput}:disabled + ${ToggleBackground} {
      background: ${theme.transparentize(theme.saturate(theme.colors.gray[900], 100), 0.8)};
      opacity: 0.5;
      cursor: default;
    }

    ${ToggleSwitch} {
      padding: ${size === 'sm' ? 6 : 8}px;
      width: ${size === 'sm' ? 6 : 8}px;
      height: ${size === 'sm' ? 6 : 8}px;
    }

    ${ToggleInput}:checked + ${ToggleBackground} {
      background: ${theme.colors.success};

      ${ToggleSwitch} {
        transition: all 0.15s ease-in-out;
        transform: translateY(-50%) translateX(175%);
      }
    }

    ${ToggleInput}:checked:focus + ${ToggleBackground} {
      box-shadow: 0 0 0 3px ${theme.transparentize(theme.colors.success, 0.85)};
    }

    ${ToggleInput}:not(:checked):focus + ${ToggleBackground} {
      box-shadow: 0 0 0 3px ${theme.transparentize(theme.colors.gray[900], 0.95)};
    }

    ${ToggleBackground} {
      width: ${size === 'sm' ? 36 : 48}px;
      height: ${size === 'sm' ? 18 : 24}px;
    }

    ${ToggleBackground} + * {
      margin-left: ${size === 'sm' ? 6 : 8}px;
    }

    * + ${ToggleInput} + ${ToggleBackground} {
      margin-left: ${size === 'sm' ? 6 : 8}px;
    }
  `}
`;
